import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  parseSearchWith,
  redirect,
  stringifySearchWith,
  type RouterHistory,
} from '@tanstack/react-router';
import { AppShell } from './app/AppShell.tsx';
import { closedPage } from './app/authorizationOnly.ts';
import {
  EngagementsPage,
  EvidencePage,
  FindingsPage,
  NotFoundPage,
  OverviewPage,
} from './app/routes/pages.tsx';
import { RootLayout } from './app/RootLayout.tsx';
import { useSession, workspaceSessionFrom } from './state/session.ts';
import { useOnboarding } from './features/onboarding/store.ts';
import { AuthorizationPage } from './features/authorization/AuthorizationPage.tsx';
import { ConfirmationPage } from './features/authorization/ConfirmationPage.tsx';
import { api, ApiError } from './features/onboarding/api.ts';
import { message, readOwner } from './features/authorization/model.ts';
import { PublicLayout } from './features/authorization/PublicLayout.tsx';
import { OwnerPage } from './features/authorization/OwnerPage.tsx';
import { VerificationPage } from './features/authorization/VerificationPage.tsx';
import { EngagementRoutePage } from './app/routes/EngagementPage.tsx';
import { HelpPage } from './app/routes/HelpPage.tsx';
import { SignInPage } from './features/auth/SignInPage.tsx';
import { expiredSessionHref } from './features/auth/sessionExpiry.ts';
import { WorkspaceAccessDenied } from './features/auth/WorkspaceAccessDenied.tsx';

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    /** Route title; the shell renders it as "<title> · Traceira". */
    title?: string;
  }
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
  // Runs on every navigation, including moves between the app's own pages.
  beforeLoad: ({ location }) => {
    if (closedPage(location.pathname)) throw redirect({ to: '/authorization', replace: true });
  },
});

// Pre-workspace onboarding (FE-036): no shell, navigation, or widgets until the
// workspace exists (DOMAIN_AUTHORIZATION_SPEC.md §1.4, rule 5).
const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/setup',
  // The query decoder turns "2" into a number, so coerce; unknown values fall back.
  // `org`: the organization a new workspace goes into, preselected in the first step.
  validateSearch: (search: Record<string, unknown>): { step: string; org?: string } => ({
    step:
      typeof search.step === 'string' || typeof search.step === 'number'
        ? String(search.step)
        : '1',
    org: typeof search.org === 'string' ? search.org : undefined,
  }),
  beforeLoad: async ({ search }) => {
    // Setup needs no account: a signed-in visitor sets up as themselves, anyone else anonymously,
    // and the email approval later hands them a session that lasts until sign-in is required.
    // Ask every time: a browser keeps the actor of an expired session, and a setup call's 401
    // must not turn into sign-in. An open workspace stays open: setup adds another one.
    try {
      useSession.getState().setActor(await api.me());
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) useSession.getState().setActor(null);
    }
    const pendingSetup = useOnboarding.getState();
    const workspaceId = pendingSetup.server.workspaceId;
    const sessionId = pendingSetup.server.sessionId ?? '';
    if (workspaceId && search.step === '1') {
      try {
        const list = await api.authorizations(workspaceId, sessionId);
        const confirmedLetter = list.letters.find((l) => Boolean(l.confirmedAt));
        if (confirmedLetter) {
          const confirmed = await api.authorizationWorkspace(
            confirmedLetter.id,
            sessionId,
            readOwner(confirmedLetter.id),
          );
          useSession.getState().start(confirmed);
          throw redirect({ to: '/', replace: true });
        }
      } catch (e) {
        if (e && typeof e === 'object' && 'to' in e) throw e;
      }
    }
  },
  component: lazyRouteComponent(
    () => import('./features/onboarding/SetupRoutes.tsx'),
    'OnboardingPage',
  ),
});

// Domain Connect return page: the DNS provider redirects here after approval.
const connectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/setup/connected',
  component: lazyRouteComponent(
    () => import('./features/onboarding/SetupRoutes.tsx'),
    'ConnectedPage',
  ),
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  validateSearch: (search: Record<string, unknown>): { redirect?: string; error?: string } => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
    error: typeof search.error === 'string' ? search.error : undefined,
  }),
  component: SignInPage,
  staticData: { title: 'Sign in' },
});

const workspacesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/workspaces',
  // `redirect`: the page a link asked for, opened once a workspace is picked.
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  // Sign-in lands here: record who signed in, so the workspace they open knows its account.
  beforeLoad: async () => {
    try {
      useSession.getState().setActor(await api.me());
    } catch {
      // The page explains a missing session through its own calls.
    }
  },
  // Workspaces and organizations share one lazy chunk, off the first load (size-limit).
  component: lazyRouteComponent(
    () => import('./features/organization/OrganizationRoutes.tsx'),
    'WorkspacesPage',
  ),
  staticData: { title: 'Workspaces' },
});

// An organization's workspaces and people; it needs an account but no open workspace.
const organizationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/organizations/$orgId',
  validateSearch: (search: Record<string, unknown>): { section?: 'members' } => ({
    section: search.section === 'members' ? 'members' : undefined,
  }),
  beforeLoad: async ({ location }) => {
    try {
      useSession.getState().setActor(await api.me());
      const { orgs } = await api.myOrgs();
      if (Array.isArray(orgs)) useSession.getState().setOrgs(orgs);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401)
        throw redirect({
          to: '/sign-in',
          search: { redirect: `${location.pathname}${location.searchStr}` },
          replace: true,
        });
    }
  },
  // Its settings, outside any workspace, from the settings chunk.
  component: lazyRouteComponent(
    () => import('./features/settings/SettingsRoutes.tsx'),
    'OrganizationHome',
  ),
  staticData: { title: 'Organization' },
});

// The command deck requires a workspace created by onboarding.
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ location, cause }) => {
    // Checked once on entering the app. Moving between its pages, or hovering a link, asks the
    // server nothing; any API call renews an expired session or ends it from here.
    if (cause !== 'enter') return;
    const session = useSession.getState();
    let actor = session.actor;
    // A 401 after a known sign-in is an expired session, which the sign-in page then explains.
    const hadActor = Boolean(actor);

    // Check identity authentication if credentials/actor exist or no workspace session is present
    if (actor || !session.workspace) {
      try {
        const previousActor = actor;
        actor = await api.me();
        // A different account signing back in must not inherit the previous workspace.
        if (previousActor && previousActor.id !== actor.id) useSession.getState().signOut();
        useSession.getState().setActor(actor);
      } catch (err: unknown) {
        if (err instanceof ApiError && err.status === 401) {
          const returnPath = `${location.pathname}${location.searchStr}`;
          // An expired session keeps its workspace and returns through the provider without
          // showing Sign in; a first visit has nothing to keep and signs in from the page.
          if (hadActor)
            throw redirect({
              href: expiredSessionHref(returnPath),
              reloadDocument: true,
              replace: true,
            });
          useSession.getState().signOut();
          throw redirect({ to: '/sign-in', search: { redirect: returnPath }, replace: true });
        }
        // If error is offline or network-related, continue with local session state (dev/test fallback)
      }
    } else if (!session.workspace.setupSessionId) {
      // A workspace saved without its account (opened right after sign-in) learns who is signed in;
      // without one it stays as it was. A setup session never had an account, so it isn't asked.
      try {
        actor = await api.me();
        useSession.getState().setActor(actor);
      } catch {
        // Not signed in: keep the setup session.
      }
    }

    // A link into a workspace (`?workspace=<id>`) opens it in this tab, checked as a switch is; a
    // refusal keeps the open workspace and says why. The address then drops the parameter.
    const linked = (location.search as { workspace?: unknown }).workspace;
    if (typeof linked === 'string' && actor && linked !== useSession.getState().workspace?.id) {
      const { prepareWorkspace } = await import('./features/workspace/switchWorkspace.ts');
      try {
        useSession.getState().setWorkspace(await prepareWorkspace(linked));
      } catch (err) {
        useSession.getState().setNotice({
          title: 'Couldn’t open the linked workspace',
          message:
            err instanceof Error && err.message
              ? err.message
              : 'You may not be a member of it. Ask one of its admins for access.',
        });
      }
    }

    let workspace = useSession.getState().workspace;

    // 2. If actor is authenticated but no workspace in session, fetch actor's workspaces
    if (!workspace && actor) {
      try {
        const { workspaces } = await api.myWorkspaces();
        useSession.getState().setWorkspaces(workspaces);
        if (workspaces.length === 0) {
          throw redirect({ to: '/workspaces', replace: true });
        } else if (workspaces.length === 1 && workspaces[0]) {
          const wsSession = workspaceSessionFrom(
            await api.workspace(workspaces[0].id),
            actor.email,
          );
          useSession.getState().start(wsSession);
          workspace = wsSession;
        } else {
          throw redirect({
            to: '/workspaces',
            search: { redirect: `${location.pathname}${location.searchStr}` },
            replace: true,
          });
        }
      } catch (e: unknown) {
        if (e && typeof e === 'object' && 'to' in e) throw e;
      }
    }

    if (!workspace?.authorizationId || !workspace.authorizationConfirmedAt) {
      const pendingSetup = useOnboarding.getState();
      const workspaceId = pendingSetup.server.workspaceId;
      const sessionId = pendingSetup.server.sessionId ?? '';
      if (workspaceId) {
        try {
          const list = await api.authorizations(workspaceId, sessionId);
          const confirmedLetter = list.letters.find((l) => Boolean(l.confirmedAt));
          if (confirmedLetter) {
            const confirmedWorkspace = await api.authorizationWorkspace(
              confirmedLetter.id,
              sessionId,
              readOwner(confirmedLetter.id),
            );
            useSession.getState().start(confirmedWorkspace);
            workspace = confirmedWorkspace;
          } else {
            throw redirect({
              to: '/setup',
              search: { step: list.letters.length > 0 ? '4' : '3' },
              replace: true,
            });
          }
        } catch (e) {
          if (e && typeof e === 'object' && 'to' in e) throw e;
          throw redirect({ to: '/setup', search: { step: '4' }, replace: true });
        }
      } else {
        if (actor) {
          throw redirect({ to: '/workspaces', replace: true });
        }
        throw redirect({ to: '/setup', search: { step: '1' }, replace: true });
      }
    }
    const authorizationId = workspace?.authorizationId;
    if (!authorizationId || !workspace) {
      throw redirect({ to: '/setup', search: { step: '1' }, replace: true });
    }
    const authToken = readOwner(authorizationId) || '';
    const confirmed = await api.authorizationWorkspace(
      authorizationId,
      workspace.setupSessionId,
      authToken,
    );
    if (confirmed.id !== workspace.id)
      throw new Error('Workspace access does not match this authorization.');
    // A saved session keeps the role and names it was saved with; entering reads them again, so a
    // changed role, a new permission or a rename shows. A failed read keeps what was saved.
    if (actor) {
      const [detail, memberOf] = await Promise.all([
        api.workspace(workspace.id).catch(() => null),
        api.myOrgs().catch(() => null),
      ]);
      if (Array.isArray(memberOf?.orgs)) useSession.getState().setOrgs(memberOf.orgs);
      const saved = useSession.getState().workspace;
      if (detail && saved?.id === detail.id)
        useSession.getState().setWorkspace({
          ...saved,
          name: detail.name,
          role: detail.role,
          permissions: detail.permissions,
          organization: detail.organization,
          via: detail.via,
        });
    }
    if (linked !== undefined) {
      const url = new URL(location.href, window.location.origin);
      url.searchParams.delete('workspace');
      throw redirect({ href: `${url.pathname}${url.search}${url.hash}`, replace: true });
    }
  },
  errorComponent: ({ error }) => {
    if (
      (error instanceof ApiError ||
        (typeof error === 'object' && error !== null && 'status' in error)) &&
      (error as { status: number }).status === 403
    ) {
      const errMsg =
        error instanceof ApiError
          ? error.message
          : typeof (error as Record<string, unknown>).message === 'string'
            ? String((error as Record<string, unknown>).message)
            : undefined;
      return (
        <WorkspaceAccessDenied workspaceId={useSession.getState().workspace?.id} message={errMsg} />
      );
    }
    return (
      <PublicLayout title="Workspace access unavailable">
        <p role="alert">{message(error)}</p>
        <p>
          <a href="/">Retry workspace access</a>
        </p>
        <p>
          <a href={`/owner/${useSession.getState().workspace?.authorizationId ?? ''}`}>
            Open owner recovery and request a new access email
          </a>
        </p>
      </PublicLayout>
    );
  },
  component: AppShell,
});

const overviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: OverviewPage,
  staticData: { title: 'Overview' },
});

function validateEvidenceSearch(search: Record<string, unknown>): Record<string, string> {
  const keys = [
    'q',
    'kind',
    'origin',
    'selected',
    'inspector',
    'id',
    'compare',
    'base',
    'target',
  ] as const;
  const result: Record<string, string> = {};
  for (const key of keys) {
    const value = search[key];
    if (typeof value === 'string' || typeof value === 'number') result[key] = String(value);
  }
  return result;
}

function validateFindingsSearch(search: Record<string, unknown>): Record<string, string> {
  const keys = [
    'q',
    'severity',
    'origin',
    'review',
    'remediation',
    'retest',
    'asset',
    'lane',
    'sort',
    'selected',
    'inspector',
    'id',
  ] as const;
  const result: Record<string, string> = {};
  for (const key of keys) {
    const value = search[key];
    if (typeof value === 'string' || typeof value === 'number') result[key] = String(value);
  }
  return result;
}

const engagementsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/engagements',
  component: EngagementsPage,
  staticData: { title: 'Engagements' },
});
// Engagement cockpit (job 004): Work tab with terminal, inspector, run header.
// All four search parameters stay plain strings; unknown values fall back with
// an explanation in the page.
const engagementRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/engagements/$engagementId',
  validateSearch: (search: Record<string, unknown>): Record<string, string> => {
    const asStr = (v: unknown) => (typeof v === 'string' || typeof v === 'number' ? String(v) : '');
    return {
      ...(search.tab !== undefined ? { tab: asStr(search.tab) } : {}),
      ...(search.view !== undefined ? { view: asStr(search.view) } : {}),
      ...(search.inspector !== undefined ? { inspector: asStr(search.inspector) } : {}),
      ...(search.id !== undefined ? { id: asStr(search.id) } : {}),
    };
  },
  component: EngagementRoutePage,
  staticData: { title: 'Work' },
});
const findingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/findings',
  validateSearch: validateFindingsSearch,
  component: FindingsPage,
  staticData: { title: 'Findings' },
});
const findingDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/findings/$findingId',
  validateSearch: validateFindingsSearch,
  // Loaded on first visit, like Reports: the review panel stays out of the initial bundle.
  component: lazyRouteComponent(
    () => import('./app/routes/FindingDetailPage.tsx'),
    'FindingDetailPage',
  ),
  staticData: { title: 'Finding detail' },
});
// Remediations: the fix cycle per finding. Both pages load on first visit, like Reports.
const remediationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/remediations',
  validateSearch: (search: Record<string, unknown>): { state?: string } =>
    typeof search.state === 'string' && search.state ? { state: search.state } : {},
  component: lazyRouteComponent(
    () => import('./app/routes/RemediationRoutes.tsx'),
    'RemediationsPage',
  ),
  staticData: { title: 'Remediations' },
});
const remediationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/remediations/$findingId',
  component: lazyRouteComponent(
    () => import('./app/routes/RemediationRoutes.tsx'),
    'RemediationPage',
  ),
  staticData: { title: 'Remediation' },
});
const evidenceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/evidence',
  validateSearch: validateEvidenceSearch,
  component: EvidencePage,
  staticData: { title: 'Evidence' },
});
const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports',
  // Loaded on first visit: the report view stays out of the initial bundle (size-limit).
  component: lazyRouteComponent(() => import('./app/routes/ReportsPage.tsx'), 'ReportsPage'),
  staticData: { title: 'Reports' },
});
// Audit logs: the workspace's timeline of who did what, loaded on first visit.
const auditLogsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/audit-logs',
  component: lazyRouteComponent(() => import('./app/routes/AuditLogsPage.tsx'), 'AuditLogsPage'),
  staticData: { title: 'Audit logs' },
});
// Settings: your account, this workspace and its organization, in one lazy chunk.
type SettingsPage = keyof typeof import('./features/settings/SettingsRoutes.tsx');
const settingsPage = (name: SettingsPage) =>
  lazyRouteComponent(() => import('./features/settings/SettingsRoutes.tsx'), name);
const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  component: settingsPage('SettingsLayout'),
  staticData: { title: 'Settings' },
});
const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/settings/workspace', replace: true });
  },
});
const profileSettingsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'profile',
  component: settingsPage('ProfileSettings'),
  staticData: { title: 'Profile' },
});
const preferencesSettingsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'preferences',
  component: settingsPage('PreferencesSettings'),
  staticData: { title: 'Preferences' },
});
const workspaceSettingsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'workspace',
  component: settingsPage('WorkspaceGeneralSettings'),
  staticData: { title: 'Workspace settings' },
});
const membersSettingsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'members',
  component: settingsPage('WorkspaceMembersSettings'),
  staticData: { title: 'Members' },
});
// A provider's install or consent screen returns here, so the path never changes.
const sourcesSettingsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'sources',
  validateSearch: (s: Record<string, unknown>): { connected?: string; error?: string } => ({
    ...(typeof s.connected === 'string' && s.connected ? { connected: s.connected } : {}),
    ...(typeof s.error === 'string' && s.error ? { error: s.error } : {}),
  }),
  component: settingsPage('SourcesSettings'),
  staticData: { title: 'Sources' },
});
const organizationSettingsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'organization',
  component: settingsPage('OrganizationGeneralSettings'),
  staticData: { title: 'Organization' },
});
const organizationMembersSettingsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'organization/members',
  component: settingsPage('OrganizationMembersSettings'),
  staticData: { title: 'Organization members' },
});
// Earlier addresses of the same pages.
const movedRoute = <P extends string>(
  path: P,
  to: '/settings/members' | '/settings/organization' | '/settings/profile',
) =>
  createRoute({
    getParentRoute: () => appRoute,
    path,
    beforeLoad: () => {
      throw redirect({ to, replace: true });
    },
  });
const movedRoutes = [
  movedRoute('/members', '/settings/members'),
  movedRoute('/organization', '/settings/organization'),
  movedRoute('/profile', '/settings/profile'),
];
const helpRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/help',
  component: HelpPage,
  staticData: { title: 'Help' },
});
const authorizationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/authorization',
  component: AuthorizationPage,
  staticData: { title: 'Authorization' },
});
const confirmationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/authorization/confirm',
  component: ConfirmationPage,
});
// Public verification starts from the customer's domain; verify.traceira.com and each customer's
// traceira-authz.<domain> page serve only this route.
const verificationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify',
  validateSearch: (search: Record<string, unknown>): { domain?: string } =>
    typeof search.domain === 'string' ? { domain: search.domain } : {},
  component: VerificationPage,
});
const ownerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/owner/$id',
  component: OwnerPage,
});
// Links sent before 2026-10-19 named a letter; its owner controls now live at /owner/:id.
const legacyVerificationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify/$id',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/owner/$id', params: { id: params.id }, replace: true });
  },
});
// Internal component reference for reviewing shared tokens and primitive states.
const componentGalleryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/component-gallery',
  // Internal reference page: loaded on visit so it never weighs on the product's first load.
  component: lazyRouteComponent(
    () => import('./app/routes/ComponentGallery.tsx'),
    'ComponentGallery',
  ),
  staticData: { title: 'Component gallery' },
});

// This route is an internal fixture harness. Vite replaces DEV with false in
// production builds, so neither the route nor its tests/gallery import ships.
const stateGalleryRoute = import.meta.env.DEV
  ? createRoute({
      getParentRoute: () => rootRoute,
      path: '/state-gallery',
      component: lazyRouteComponent(
        () => import('../tests/gallery/StateGallery.tsx'),
        'StateGallery',
      ),
      staticData: { title: 'Network state gallery · internal fixtures' },
    })
  : null;

const routeTree = rootRoute.addChildren([
  setupRoute,
  connectedRoute,
  signInRoute,
  workspacesRoute,
  organizationRoute,
  confirmationRoute,
  verificationRoute,
  ownerRoute,
  legacyVerificationRoute,
  componentGalleryRoute,
  ...(stateGalleryRoute ? [stateGalleryRoute] : []),
  appRoute.addChildren([
    overviewRoute,
    engagementsRoute,
    engagementRoute,
    findingsRoute,
    findingDetailRoute,
    remediationsRoute,
    remediationRoute,
    evidenceRoute,
    reportsRoute,
    auditLogsRoute,
    settingsRoute.addChildren([
      settingsIndexRoute,
      profileSettingsRoute,
      preferencesSettingsRoute,
      workspaceSettingsRoute,
      membersSettingsRoute,
      sourcesSettingsRoute,
      organizationSettingsRoute,
      organizationMembersSettingsRoute,
    ]),
    ...movedRoutes,
    helpRoute,
    authorizationRoute,
  ]),
]);

export function createAppRouter(history?: RouterHistory) {
  return createRouter({
    routeTree,
    history,
    defaultPreload: 'intent',
    scrollRestoration: true,
    // Search values are plain strings (?step=2), not JSON (spec §2.2).
    parseSearch: parseSearchWith((value) => value),
    stringifySearch: stringifySearchWith((value) => String(value)),
  });
}
